import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useB3Lang } from '@b3/lang';
import { Box, InputAdornment, TextField, Typography } from '@mui/material';
import cloneDeep from 'lodash-es/cloneDeep';

import { B2BAutoCompleteCheckbox } from '@/components';
import B3Spin from '@/components/spin/B3Spin';
import { B3PaginationTable } from '@/components/table/B3PaginationTable';
import { TableColumnItem } from '@/components/table/B3Table';
import { permissionLevels } from '@/constants';
import { useMobile, useSort } from '@/hooks';
import { getCompanyData } from '@/shared/service/b2b';
import { fetchInvoices } from '@/shared/service/request/alumio';
import { rolePermissionSelector, useAppSelector } from '@/store';
import { CustomerRole } from '@/types';
import { InvoiceList, InvoiceListNode } from '@/types/invoice';
import {
  b2bPermissionsMap,
  currencyFormatInfo,
  displayFormat,
  getUTCTimestamp,
  handleGetCorrespondingCurrencyToken,
  snackbar,
  validatePermissionWithComparisonType,
} from '@/utils';
import { convertODataDate } from '@/utils/b3DateFormat';
import b2bLogger from '@/utils/b3Logger';

import B3Filter from '../../components/filter/B3Filter';

import B3Pulldown from './components/B3Pulldown';
import { InvoiceDetailCard } from './components/InvoiceDetailCard';
import InvoiceFooter from './components/InvoiceFooter';
import InvoiceStatus from './components/InvoiceStatus';
import PaymentsHistory from './components/PaymentsHistory';
import PaymentSuccess from './components/PaymentSuccess';
import InvoiceListType, {
  defaultSortKey,
  filterFormConfig,
  filterFormConfigsTranslationVariables,
  sortIdArr,
} from './utils/config';
import { formattingNumericValues } from './utils/payment';
import { handlePrintSapPDF } from './utils/pdf';
import { InvoiceItemCard } from './InvoiceItemCard';

export interface FilterSearchProps {
  [key: string]: string | number | number[] | null;
  q: string;
  startValue: number | string;
  endValue: number | string;
  companyIds: number[];
  invoiceDateFrom: number | string;
  invoiceDateTo: number | string;
  offset: number;
  first: number;
  orderNumber: string;
  deliveryNumber: string;
}

interface PaginationTableRefProps extends HTMLInputElement {
  getList: () => void;
  getCacheList: () => void;
  setCacheAllList: (items?: InvoiceList[]) => void;
  setList: (items?: InvoiceListNode[]) => void;
  getSelectedValue: () => void;
}

interface ExtraField {
  fieldName: string;
  fieldValue: string;
}

interface Item {
  ObjectID: string;
}

interface InvoiceData {
  ID: string;
  Item?: Item[];
}

const initFilter = {
  q: '',
  first: 10,
  offset: 0,
  orderBy: `-${sortIdArr[defaultSortKey]}`,
  companyIds: [],
  orderNumber: '',
};

function Invoice() {
  const hasAppliedUrlFilter = useRef(false);
  const currentDate = new Date().getTime();
  const b3Lang = useB3Lang();
  const role = useAppSelector(({ company }) => company.customer.role);
  const isAgenting = useAppSelector(({ b2bFeatures }) => b2bFeatures.masqueradeCompany.isAgenting);

  const companyInfoId = useAppSelector(({ company }) => company.companyInfo.id);
  const { selectCompanyHierarchyId, isEnabledCompanyHierarchy } = useAppSelector(
    ({ company }) => company.companyHierarchyInfo,
  );
  const salesRepCompanyId = useAppSelector(({ b2bFeatures }) => b2bFeatures.masqueradeCompany.id);
  const currentCompanyId =
    role === CustomerRole.SUPER_ADMIN && isAgenting
      ? Number(salesRepCompanyId)
      : Number(companyInfoId);

  const { invoicePayPermission, purchasabilityPermission } = useAppSelector(rolePermissionSelector);

  const { invoice: invoiceSubViewPermission } = useAppSelector(
    ({ company }) => company.pagesSubsidiariesPermission,
  );

  const navigate = useNavigate();
  const [isMobile] = useMobile();
  const paginationTableRef = useRef<PaginationTableRefProps | null>(null);

  const { decimal_places: decimalPlaces = 2 } = currencyFormatInfo();

  const [isRequestLoading, setIsRequestLoading] = useState<boolean>(false);
  const [isOpenHistory, setIsOpenHistory] = useState<boolean>(false);
  const [currentInvoiceId, setCurrentInvoiceId] = useState<string>('');
  const [receiptId, setReceiptId] = useState<string>('');
  const [type, setType] = useState<string>('');
  // const [unpaidAmount, setUnpaidAmount] = useState<number>(0);
  // const [overdueAmount, setOverdueAmount] = useState<number>(0);
  const [checkedArr, setCheckedArr] = useState<CustomFieldItems | InvoiceListNode[]>([]);
  const [selectedPay, setSelectedPay] = useState<CustomFieldItems | InvoiceListNode[]>([]);
  const [list, setList] = useState<InvoiceListNode[]>([]);

  const [filterData, setFilterData] = useState<Partial<FilterSearchProps> | null>();

  const [filterChangeFlag, setFilterChangeFlag] = useState(false);
  const [filterLists, setFilterLists] = useState<InvoiceListNode[]>([]);

  const invoiceSubPayPermission = validatePermissionWithComparisonType({
    level: permissionLevels.COMPANY_SUBSIDIARIES,
    code: b2bPermissionsMap.invoicePayPermission,
  });

  const [handleSetOrderBy, order, orderBy] = useSort(
    sortIdArr,
    defaultSortKey,
    filterData,
    setFilterData,
  );

  const location = useLocation();

  const isFiltering = (filterData: Partial<FilterSearchProps>) =>
    Object.keys(filterData).some(
      (key) => key !== 'first' && key !== 'offset' && key !== 'orderBy' && filterData[key],
    );

  const cacheFilterLists = useCallback(
    (edges: InvoiceListNode[]) => {
      setFilterLists((prevFilterLists) => {
        if (filterChangeFlag) {
          setFilterChangeFlag(false);
          return edges;
        }

        if (!prevFilterLists.length) return edges;

        const copyCacheFilterList = [...prevFilterLists];

        edges.forEach((item: InvoiceListNode) => {
          const option = item?.node || item;
          const isExist = prevFilterLists.some((cache: InvoiceListNode) => {
            const cacheOption = cache.node;
            return cacheOption.id === option.id;
          });

          if (!isExist) {
            copyCacheFilterList.push(item);
          }
        });

        return copyCacheFilterList;
      });
    },
    [filterChangeFlag],
  );

  // const handleStatisticsInvoiceAmount = async () => {
  //   try {
  //     setIsRequestLoading(true);
  //     // const { invoiceStats } = await getInvoiceStats(
  //     //   filterData?.status ? +filterData.status : 0,
  //     //   +decimalPlaces,
  //     //   filterData?.companyIds || [],
  //     // );

  //     // if (invoiceStats) {
  //     //   const { overDueBalance, totalBalance } = invoiceStats;
  //     //   setUnpaidAmount(+formattingNumericValues(+totalBalance, decimalPlaces));
  //     //   setOverdueAmount(+formattingNumericValues(+overDueBalance, decimalPlaces));
  //     // }
  //   } catch (err) {
  //     b2bLogger.error(err);
  //   } finally {
  //     setIsRequestLoading(false);
  //   }
  // };

  const handleChange = (key: string, value: string) => {
    if (key === 'search') {
      setFilterData({
        ...filterData,
        q: value,
      });
      setFilterChangeFlag(true);
      setType(InvoiceListType.NORMAL);
    }
  };

  const handleFilterChange = (value: Partial<FilterSearchProps>) => {
    const startValue = value?.startValue
      ? getUTCTimestamp(new Date(value?.startValue).getTime() / 1000)
      : '';

    const endValue = value?.endValue
      ? getUTCTimestamp(new Date(value?.endValue).getTime() / 1000, true)
      : '';

    const invoiceDateFromValue = value?.invoiceDateFrom
      ? getUTCTimestamp(new Date(value?.invoiceDateFrom).getTime() / 1000)
      : '';

    const invoiceDateToValue = value?.invoiceDateTo
      ? getUTCTimestamp(new Date(value?.invoiceDateTo).getTime() / 1000, true)
      : '';

    const status = 0;
    const releaseStatus = value?.releaseStatus;

    const search: Partial<FilterSearchProps> = {
      status: `${status}` || '',
      releaseStatus: `${releaseStatus}` || '',
      invoiceID: value?.invoiceID,
      beginDateAt: startValue,
      endDateAt: endValue,
      beginDueDateAt: value?.status === 0 ? parseInt(`${currentDate / 1000}`, 10) : '',
      endDueDateAt: value?.status === 3 ? parseInt(`${currentDate / 1000}`, 10) : '',
      invoiceDateFrom: invoiceDateFromValue,
      invoiceDateTo: invoiceDateToValue,
    };

    setFilterData({
      ...filterData,
      ...search,
    });
    setFilterChangeFlag(true);
    setType(InvoiceListType.NORMAL);
  };

  const getSelectCheckbox = (selectCheckbox: Array<string | number>) => {
    if (selectCheckbox.length > 0) {
      const productList = paginationTableRef.current?.getCacheList() || [];

      const checkedItems = selectCheckbox.map((item: number | string) => {
        const newItems = productList.find((product: InvoiceListNode) => {
          const { node } = product;

          return Number(node.id) === Number(item);
        });

        return newItems;
      });

      const newEnableItems = checkedItems.filter(
        (item: InvoiceListNode | undefined) => item && !item.node.disableCurrentCheckbox,
      );
      setCheckedArr([...newEnableItems]);
    } else {
      setCheckedArr([]);
    }
  };

  const handleViewInvoice = async (invoiceId: string) => {
    try {
      setIsRequestLoading(true);
      const pdfUrl = await handlePrintSapPDF(invoiceId);

      if (!pdfUrl) {
        snackbar.error(b3Lang('invoice.pdfUrlResolutionError'));
        return;
      }

      const { href } = window.location;
      if (!href.includes('invoice')) {
        return;
      }

      window.open(pdfUrl, '_blank', 'fullscreen=yes');
    } catch (err) {
      b2bLogger.error(err);
    } finally {
      setIsRequestLoading(false);
    }
  };

  const handleSetSelectedInvoiceAccount = (newPrice: number | string, invoiceId: string) => {
    const currentOriginInvoice = checkedArr.find((invoice: InvoiceListNode) => {
      const {
        node: { id },
      } = invoice;

      return Number(id) === Number(invoiceId);
    });

    if (selectedPay.length > 0) {
      const newInvoices = selectedPay.map((selectedItem: InvoiceListNode) => {
        const {
          node: { id, openBalance },
        } = selectedItem;
        const {
          node: { openBalance: currentOriginOpenBalance },
        } = currentOriginInvoice;

        if (Number(id) === Number(invoiceId)) {
          openBalance.value =
            Number(currentOriginOpenBalance.value) < Number(newPrice)
              ? currentOriginOpenBalance.value
              : newPrice;
        }

        return selectedItem;
      });

      setSelectedPay(newInvoices);
    }
  };

  useEffect(() => {
    const newInitFilter = {
      ...initFilter,
      companyIds: [Number(selectCompanyHierarchyId) || Number(currentCompanyId)],
    };
    if (location?.search) {
      const params = new URLSearchParams(location.search);
      const getInvoiceId = params.get('invoiceId') || '';
      const getReceiptId = params.get('receiptId') || '';

      if (getInvoiceId) {
        setFilterData({
          ...newInitFilter,
          q: getInvoiceId,
        });
        setType(InvoiceListType.DETAIL);
      }

      if (getReceiptId) {
        // open Successful page
        setType(InvoiceListType.CHECKOUT);
        setFilterData({
          ...newInitFilter,
        });
        setReceiptId(getReceiptId);
      }
    } else {
      setType(InvoiceListType.NORMAL);
      setFilterData({
        ...newInitFilter,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, selectCompanyHierarchyId]);

  const handleSelectCompanies = (company: number[]) => {
    const newCompanyIds = company.includes(-1) ? [] : company;
    setFilterData({
      ...filterData,
      companyIds: newCompanyIds,
    });
  };

  useEffect(() => {
    const selectedInvoice =
      checkedArr.filter((item: InvoiceListNode) => {
        const {
          node: { openBalance },
        } = item;

        return Number(openBalance.value) !== 0;
      }) || [];

    if (selectedInvoice.length > 0) {
      if (selectedPay.length === 0) {
        setSelectedPay(cloneDeep(selectedInvoice));
      } else {
        const newArr = selectedInvoice.map((checkedItem: InvoiceListNode) => {
          const {
            node: { id, openBalance },
          } = checkedItem;

          const currentSelectedItem = selectedPay.find((item: InvoiceListNode) => {
            const {
              node: { id: selectedId },
            } = item;

            return Number(id) === Number(selectedId);
          });

          if (currentSelectedItem) {
            const {
              node: { openBalance: currentOpenBalance },
            } = currentSelectedItem;

            openBalance.value = currentOpenBalance.value;
          }

          return checkedItem;
        });

        setSelectedPay(cloneDeep(newArr));
      }
    } else {
      setSelectedPay([]);
    }
    // ignore selectedPay cause it will trigger an useEffect loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkedArr]);

  const fetchList = useCallback(
    async (params: Partial<FilterSearchProps>): Promise<{ edges: any[]; totalCount: number }> => {
      // TODO: Change interface
      let data: any[] = [];
      let totalCount = 0;

      if (!hasAppliedUrlFilter.current) {
        const searchParams = new URLSearchParams(location.search);
        const invoiceIdFromUrl = searchParams.get('invoiceId') || '';
        console.log('invoiceIdFromUrl: ', invoiceIdFromUrl);

        if (invoiceIdFromUrl) {
          params.invoiceID = invoiceIdFromUrl;

          setTimeout(() => {
            const filterTriggerEvent = new CustomEvent('b3-filter-applied', {
              detail: {
                invoiceID: invoiceIdFromUrl,
              },
            });
            window.dispatchEvent(filterTriggerEvent);
          }, 0);
        }

        hasAppliedUrlFilter.current = true;
      }

      try {
        console.log('params: ', params);

        const resCompany = await getCompanyData(currentCompanyId);
        const accountId = resCompany?.data?.extraFields?.find(
          (field: ExtraField) => field.fieldName === 'account_id',
        )?.fieldValue;

        if (!accountId) {
          console.error('Account ID not found in extraFields.');
          return { edges: [], totalCount: 0 };
        }

        const response = await fetchInvoices(accountId, params, params?.offset, params?.first);

        if (!response) {
          return { edges: [], totalCount: 0 };
        }

        const invoiceItemMap: Record<string, string> = {};

        response.results.forEach((invoice: InvoiceData) => {
          const invoiceId = invoice.ID;
          const item = invoice.Item?.[0];

          if (item) {
            invoiceItemMap[invoiceId] = item.ObjectID;
          }
        });

        const { __count: count, results = [] } = response;

        totalCount = parseInt(count, 10);
        data = results;

        const edges = data.map((invoice: any) => {
          const buyerId = invoice?.BuyerParty?.PartyID;
          const invoiceId = invoice?.ID;
          const invoiceCreationDate = invoice?.CreationDateTime;
          const invoiceDueDate = invoice?.CashDiscountTerms.FullPaymentEndDate;
          const totalCurrencyCode = invoice?.TotalGrossAmountCurrencyCode;
          const totalAmount = invoice?.TotalGrossAmount;
          const invoiceStatus = invoice?.ConsistencyStatusCode;
          const invoiceAmountToPay = invoiceStatus === 3 ? 0 : totalAmount; // TODO:Improve this logic to get the right value
          const releaseStatusCode = invoice?.ReleaseStatusCode;

          let salesOrderIds: string[] = [];
          let deliveryIds: string[] = [];

          const items = invoice?.Item || [];

          items.forEach((item: any) => {
            const refs = item.ItemBusinessTransactionDocumentReference || [];

            refs.forEach((ref: any) => {
              if (ref.ItemTypeCode === '28' && ref.ID) {
                salesOrderIds.push(ref.ID);
              } else if (ref.ItemTypeCode === '14' && ref.ID) {
                deliveryIds.push(ref.ID);
              }
            });
          });

          salesOrderIds = [...new Set(salesOrderIds)];
          deliveryIds = [...new Set(deliveryIds)];

          return {
            node: {
              companyInfo: {
                companyId: buyerId,
              },
              createdAt: invoiceCreationDate,
              customerId: buyerId,
              disableCurrentCheckbox: false,
              dueDate: invoiceDueDate,
              externalId: null,
              id: invoiceId,
              invoiceNumber: invoiceId,
              notAllowedPay: false,
              openBalance: {
                code: totalCurrencyCode,
                value: invoiceAmountToPay,
                originValue: invoiceAmountToPay,
              },
              orderNumber: salesOrderIds,
              deliveryNumber: deliveryIds,
              orderUserId: '',
              originalBalance: {
                code: totalCurrencyCode,
                value: totalAmount,
              },
              pendingPaymentCount: 0,
              purchaseOrderNumber: '',
              status: releaseStatusCode,
              updatedAt: invoiceCreationDate,
            },
          };
        });

        const invoicesList: any[] = edges;

        invoicesList.forEach((invoiceNode: InvoiceListNode) => {
          const {
            node: { openBalance },
          } = invoiceNode;
          const item = invoiceNode;
          item.node.disableCurrentCheckbox = false;

          openBalance.originValue = `${Number(openBalance.value)}`;
          openBalance.value = formattingNumericValues(Number(openBalance.value), decimalPlaces);

          item.node.disableCurrentCheckbox = Number(openBalance.value) === 0;

          const { companyInfo } = item.node;
          if (Number(companyInfo.companyId) !== Number(currentCompanyId)) {
            item.node.disableCurrentCheckbox =
              !invoiceSubPayPermission || Number(openBalance.value) === 0;
          }
        });
        setList(invoicesList);
        // handleStatisticsInvoiceAmount();

        if (filterData && isFiltering(filterData) && invoicesList.length) {
          cacheFilterLists(invoicesList);
        } else {
          setFilterLists([]);
        }
        return {
          edges: invoicesList,
          totalCount,
        };
      } catch (e) {
        console.log(e);
        return {
          edges: [],
          totalCount: 0,
        };
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentCompanyId, cacheFilterLists, decimalPlaces, filterData, invoiceSubPayPermission],
  );

  const handleSetSelectedInvoiceAccountNumber = (val: string, id: string) => {
    let result = val;
    if (val.includes('.')) {
      const wholeDecimalNumber = val.split('.');
      const movePoint =
        decimalPlaces === 0 ? 0 : wholeDecimalNumber[1].length - Number(decimalPlaces);
      if (wholeDecimalNumber[1] && movePoint > 0) {
        const newVal = wholeDecimalNumber[0] + wholeDecimalNumber[1];
        result = `${newVal.slice(0, -decimalPlaces)}.${newVal.slice(-decimalPlaces)}`;
      }
      if (wholeDecimalNumber[1] && movePoint === 0) {
        result = formattingNumericValues(Number(val), decimalPlaces);
      }
    } else if (result.length > 1) {
      result = `${val.slice(0, 1)}.${val.slice(-1)}`;
      if (Number(decimalPlaces) === 0) result = `${val}`;
    } else {
      result = `${val}`;
    }

    handleSetSelectedInvoiceAccount(result, id);
  };

  const columnAllItems: TableColumnItem<InvoiceList>[] = [
    {
      key: 'id',
      title: b3Lang('invoice.headers.invoice'),
      isSortable: true,
      render: (item: InvoiceList) => (
        <Box
          sx={{
            color: '#000000',
            cursor: 'pointer',
            ':hover': {
              textDecoration: 'underline',
            },
          }}
          onClick={() => {
            handleViewInvoice(item?.id);
          }}
        >
          {item?.invoiceNumber ? item?.invoiceNumber : item?.id}
        </Box>
      ),
      width: '8%',
    },
    {
      key: 'orderNumber',
      title: b3Lang('invoice.headers.order'),
      isSortable: false,
      render: (item: InvoiceList) => {
        const orderNumbers = item?.orderNumber;

        if (!orderNumbers || orderNumbers.length === 0) return '-';

        return (
          <Box>
            {orderNumbers.map((id: string) => (
              <Box
                key={id}
                sx={{
                  color: '#000000',
                  cursor: 'pointer',
                  textDecoration: 'none',
                  ':hover': {
                    textDecoration: 'underline',
                  },
                }}
                onClick={(event) => {
                  event.stopPropagation();
                  navigate(`/company-orders?orderId=${id}`);
                }}
              >
                {id}
              </Box>
            ))}
          </Box>
        );
      },
      width: '12%',
    },
    {
      key: 'deliveryNumber',
      title: b3Lang('invoice.headers.deliveryid'),
      isSortable: false,
      render: (item: InvoiceList) => {
        const deliveryIds = item?.deliveryNumber;

        if (!deliveryIds || deliveryIds.length === 0) return '-';

        return (
          <Box>
            {deliveryIds.map((id: string) => (
              <Box
                key={id}
                sx={{
                  color: '#000000',
                  cursor: 'pointer',
                  textDecoration: 'none',
                  ':hover': {
                    textDecoration: 'underline',
                  },
                }}
                onClick={(event) => {
                  event.stopPropagation();
                  navigate(`/deliveries?deliveryId=${id}`);
                }}
              >
                {id}
              </Box>
            ))}
          </Box>
        );
      },
      width: '12%',
    },
    {
      key: 'createdAt',
      title: b3Lang('invoice.headers.invoiceDate'),
      isSortable: true,
      render: (item: InvoiceList) => {
        const date = convertODataDate(item?.createdAt);

        const timestampInSeconds = Math.floor(new Date(date).getTime() / 1000);

        return `${displayFormat(Number(timestampInSeconds))}`;
      },
      width: '15%',
    },
    {
      key: 'updatedAt',
      title: b3Lang('invoice.headers.dueDate'),
      isSortable: false,

      render: (item: InvoiceList) => {
        const { status } = item;

        const date = convertODataDate(item?.dueDate);
        const timestampInSeconds = Math.floor(new Date(date).getTime() / 1000);
        const isOverdue = currentDate > timestampInSeconds * 1000 && status !== 2;

        return (
          <Typography
            sx={{
              color: isOverdue ? '#D32F2F' : 'rgba(0, 0, 0, 0.87)',
              fontSize: '14px',
            }}
          >
            {`${item.dueDate ? displayFormat(Number(timestampInSeconds)) : '–'}`}
          </Typography>
        );
      },
      width: '15%',
    },
    {
      key: 'originalBalance',
      title: b3Lang('invoice.headers.invoiceTotal'),
      isSortable: true,
      render: (item: InvoiceList) => {
        const { originalBalance } = item;
        const originalAmount = formattingNumericValues(
          Number(originalBalance.value),
          decimalPlaces,
        );

        const token = handleGetCorrespondingCurrencyToken(originalBalance.code);

        return `${token}${originalAmount || 0}`;
      },
      width: '10%',
    },
    {
      key: 'openBalanceToPay',
      title: b3Lang('invoice.headers.amountToPay'),
      render: (item: InvoiceList) => {
        const { openBalance, id } = item;
        const currentCode = openBalance.code || 'USD';
        let valuePrice = openBalance.value;
        let disabled = true;

        if (selectedPay.length > 0) {
          const currentSelected = selectedPay.find((item: InvoiceListNode) => {
            const {
              node: { id: selectedId },
            } = item;

            return Number(selectedId) === Number(id);
          });

          if (currentSelected) {
            const {
              node: { openBalance: selectedOpenBalance },
            } = currentSelected;

            disabled = false;
            valuePrice = selectedOpenBalance.value;

            if (Number(openBalance.value) === 0) {
              disabled = true;
            }
          }
        }

        return (
          <TextField
            disabled={disabled}
            variant="filled"
            value={valuePrice || ''}
            InputProps={{
              startAdornment: (
                <InputAdornment
                  position="start"
                  sx={{ padding: '8px 0', marginTop: '0 !important' }}
                >
                  {handleGetCorrespondingCurrencyToken(currentCode)}
                </InputAdornment>
              ),
            }}
            sx={{
              '& input': {
                paddingTop: '8px',
              },
              '& input[type="number"]::-webkit-inner-spin-button, & input[type="number"]::-webkit-outer-spin-button':
                {
                  WebkitAppearance: 'none',
                  margin: 0,
                },
            }}
            onChange={(e: CustomFieldItems) => {
              const val = e.target?.value;
              handleSetSelectedInvoiceAccountNumber(val, id);
            }}
            type="number"
          />
        );
      },
      width: '15%',
    },
    {
      key: 'status',
      title: b3Lang('invoice.headers.status'),
      isSortable: false,
      render: (item: InvoiceList) => {
        return <InvoiceStatus code={item.status} />;
      },
    },
    {
      key: 'companyName',
      title: b3Lang('invoice.headers.action'),
      render: (row: InvoiceList) => {
        const { id, companyInfo } = row;
        let actionRow = row;
        if (selectedPay.length > 0) {
          const currentSelected = selectedPay.find((item: InvoiceListNode) => {
            const {
              node: { id: selectedId },
            } = item;

            return Number(selectedId) === Number(id);
          });

          if (currentSelected) {
            actionRow = currentSelected.node;
          }
        }

        return (
          <B3Pulldown
            row={actionRow}
            setInvoiceId={setCurrentInvoiceId}
            handleOpenHistoryModal={setIsOpenHistory}
            setIsRequestLoading={setIsRequestLoading}
            isCurrentCompany={Number(currentCompanyId) === Number(companyInfo.companyId)}
            invoicePay={
              Number(currentCompanyId) === Number(companyInfo.companyId)
                ? invoicePayPermission
                : invoiceSubPayPermission
            }
          />
        );
      },
      width: '10%',
    },
  ];

  const translatedFilterFormConfigs = filterFormConfig.map((element) => {
    const config = element;
    config.label = b3Lang(element.label);

    config.options = element.options.map((option) => {
      const elementOption = option;
      elementOption.label = b3Lang(filterFormConfigsTranslationVariables[option.key]);

      return option;
    });

    return element;
  });

  return (
    <B3Spin isSpinning={isRequestLoading}>
      <Box
        sx={{
          overflowX: 'auto',
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          position: 'relative',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: isMobile ? 'flex-start' : 'center',
            flexDirection: isMobile ? 'column' : 'row',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: isMobile ? 'flex-start' : 'center',
              flexDirection: isMobile ? 'column' : 'row',
              width: isMobile ? '100%' : 'auto',

              '& > div': {
                width: isMobile ? '100%' : 'auto',
              },
            }}
          >
            {isEnabledCompanyHierarchy && invoiceSubViewPermission && (
              <Box
                sx={{
                  mr: isMobile ? 0 : '10px',
                  mb: '30px',
                }}
              >
                <B2BAutoCompleteCheckbox handleChangeCompanyIds={handleSelectCompanies} />
              </Box>
            )}
            <B3Filter
              filterMoreInfo={translatedFilterFormConfigs}
              handleChange={handleChange}
              handleFilterChange={handleFilterChange}
              startPicker={{
                isEnabled: true,
                label: b3Lang('invoice.filter.invoiceDueDateFrom'),
                defaultValue:
                  typeof filterData?.beginDateAt === 'number'
                    ? Number(filterData.beginDateAt) * 1000
                    : '',
                pickerKey: 'start',
              }}
              endPicker={{
                isEnabled: true,
                label: b3Lang('invoice.filter.invoiceDueDateTo'),
                defaultValue:
                  typeof filterData?.endDateAt === 'number'
                    ? Number(filterData.endDateAt) * 1000
                    : '',
                pickerKey: 'end',
              }}
              searchValue={filterData?.q || ''}
              pcContainerWidth="36rem"
              pcSearchContainerWidth="80%"
            />
          </Box>
          <Box
            sx={{
              display: 'flex',
              marginBottom: '30px',
              flexDirection: document.body.clientWidth <= 465 ? 'column' : 'row',
            }}
          />
        </Box>
        <B3PaginationTable
          ref={paginationTableRef}
          columnItems={columnAllItems}
          rowsPerPageOptions={[10, 20, 30]}
          getRequestList={fetchList as any}
          searchParams={filterData as any}
          isCustomRender={false}
          requestLoading={setIsRequestLoading}
          tableKey="id"
          getSelectCheckbox={getSelectCheckbox}
          CollapseComponent={InvoiceDetailCard}
          sortDirection={order}
          orderBy={orderBy}
          sortByFn={handleSetOrderBy}
          isSelectOtherPageCheckbox
          hover
          isAutoRefresh={false}
          renderItem={(row: InvoiceList, index?: number) => (
            <InvoiceItemCard
              item={row}
              handleSetSelectedInvoiceAccount={handleSetSelectedInvoiceAccountNumber}
              handleViewInvoice={handleViewInvoice}
              setIsRequestLoading={setIsRequestLoading}
              setInvoiceId={setCurrentInvoiceId}
              handleOpenHistoryModal={setIsOpenHistory}
              selectedPay={selectedPay}
              handleGetCorrespondingCurrency={handleGetCorrespondingCurrencyToken}
              addBottom={list.length - 1 === index}
              isCurrentCompany={Number(currentCompanyId) === Number(row.companyInfo.companyId)}
              invoicePay={
                Number(currentCompanyId) === Number(row.companyInfo.companyId)
                  ? invoicePayPermission
                  : invoiceSubPayPermission
              }
            />
          )}
        />
      </Box>
      {selectedPay.length > 0 &&
        (((invoicePayPermission || invoiceSubPayPermission) && purchasabilityPermission) ||
          isAgenting) && <InvoiceFooter selectedPay={selectedPay} decimalPlaces={decimalPlaces} />}
      <PaymentsHistory
        open={isOpenHistory}
        currentInvoiceId={currentInvoiceId}
        setOpen={setIsOpenHistory}
      />
      <PaymentSuccess receiptId={Number(receiptId)} type={type} />
    </B3Spin>
  );
}

export default Invoice;
